import React, { useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { shelfService } from "/app/src/services";
import { Search } from "/app/src/components/generic/components/search";
import { useTranslation } from "react-i18next";
import { Shelf as ShelfType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import useQueryParams from "/app/src/hooks/useQueryParams";
/**
 * Shelves component displays a list of shelves with search, pagination, and sorting functionalities.
 *
 * @returns {JSX.Element} The rendered Shelves component.
 */
export default function Shelves() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const columnHelper = createColumnHelper<ShelfType>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("fullName", {
        id: "fullName",
        cell: (info) => info.getValue() || info.row.original.name,
        header: () => t("translation:name"),
      }),
      columnHelper.accessor("number", {
        id: "number",
        cell: (info) => info.getValue(),
        header: () => t("translation:number"),
      }),
      columnHelper.accessor("sequenceNumber", {
        id: "sequenceNumber",
        cell: (info) => info.getValue(),
        header: () => t("translation:sequence_number"),
      }),
    ],
    [columnHelper, t],
  );

  const [sort, setSort] = useSortUpgrade([]);
  const [searchParams] = useSearchParams();

  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  /**
   * Navigates to the shelf details view for a given shelf ID.
   *
   * @param {number} shelfId - The unique identifier of the shelf.
   */
  const goToShelf = useCallback(
    (shelfId: number) => {
      navigate(`/explorer/shelves/${shelfId}`);
    },
    [navigate],
  );

  const {
    data: shelves,
    count: shelvesCount,
    isFetching,
  } = usePaginatedData<ShelfType>({
    queryKey: ["shelves"],
    searchString,
    sort,
    page,
    pageSize,
    service: shelfService,
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: shelvesCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:shelves")} - ItemPath</title>
      </Helmet>
      <Search allowClear setSearchString={setSearchString} />
      <Table
        loading={isFetching}
        rows={shelves}
        tableColumns={columns}
        length={shelvesCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPageSize: updatePageSize,
          setPage: updatePage,
        }}
        enableRowSelection
        rowClicked={goToShelf}
        emptyText={t("translation:no_shelves_found")}
      />
    </div>
  );
}
